'use client';

import SearchBox from 'components/SearchBox';
import { useState, useEffect, useRef, useCallback } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { PostgrestError } from '@supabase/supabase-js';
import type { Example } from 'types/Example';
import { Button } from 'components/ui/button';
import { CornerDownLeft } from 'lucide-react';
import { createBrowserClientSupa } from '@/utils/supabase/createBrowserClient';
import SearchResults from './SearchResults';
import Spinner from './Spinner';

const Search = () => {
  const [error, setError] = useState<PostgrestError | null>(null);
  const [results, setResults] = useState<Example[]>([]);
  const [searchTerm, setSearchterm] = useState('');
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const searchbox = useRef(null);
  const searchTermInUrl = searchParams.get('s');
  const supabase = createBrowserClientSupa();
  const [searched, setSearched] = useState(false);
  const bookmarkedNotesIds = [] as string[];
  const archivedNotesIds = [] as string[];

  // Update the url with whatever is on the input box
  // this should automatically trigger a search
  const imperativeSearch = useCallback(() => {
    const params = new URLSearchParams(searchParams);

    params.set('s', searchTerm);
    const href = `${pathname}?${params.toString()}`;
    router.replace(href);
  }, [searchTerm, router, pathname, searchParams]);

  const clearError = () => setError(null);

  const enhancedResults = results.map((example) => ({
    ...example,
    archived: archivedNotesIds.includes(example.id),
    bookmarked: bookmarkedNotesIds.includes(example.id),
  }));

  const executeSearch = useCallback(
    async (term: string) => {
      const sanitizedTerm = term.trim();
      setResults([]);
      setLoading(true);
      if (!sanitizedTerm) {
        setSearched(false);
        return;
      }

      const { data, error } = await supabase
        .from('examples')
        .select('*, base_commands(*)')
        .or(
          `example.ilike.%${sanitizedTerm}%,short_description.ilike.%${sanitizedTerm}%`,
        )
        .eq('draft', false)
        .order('example', { ascending: false });

      setResults(data as Example[]);
      setLoading(false);
      setError(error);
      setSearched(true);
    },
    [supabase],
  );

  useEffect(() => {
    if (searchTermInUrl) {
      executeSearch(searchTermInUrl);
      setSearchterm(searchTermInUrl);
    }
  }, [executeSearch, searchTermInUrl]);

  const errorMessage = error ? (
    <div className="relative rounded border border-red-400 bg-red-100 px-4 py-3 text-red-700">
      <p>There has been an error</p>
      <button
        className="text-blue-500 hover:text-blue-700"
        onClick={clearError}
      >
        Clear
      </button>
    </div>
  ) : null;

  return (
    <div className="gcontainer mx-auto max-w-3xl px-4">
      <div className="flex-col space-y-4">
        <div className="flex w-full items-center justify-center space-x-2 px-2 sm:px-0">
          <SearchBox
            ref={searchbox}
            searchTerm={searchTerm}
            onChangeSearchTerm={setSearchterm}
            onEnter={imperativeSearch}
          />
          <Button className="hidden sm:block" onClick={imperativeSearch}>
            Search
          </Button>
          <Button className="sm:hidden" size="sm" onClick={imperativeSearch}>
            <CornerDownLeft size={16} />
          </Button>
        </div>
        {errorMessage}
        <SearchResults
          results={enhancedResults}
          loading={loading}
          searchTerm={searchTerm}
        />
        <Spinner loading={loading} />
      </div>
    </div>
  );
};

export default Search;
